import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Space, Modal, Input, message, Popconfirm, Tag, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, StarOutlined, StarFilled } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getSessionUser } from "../../../utils/auth/sessionUser";

import {
	EmailSignature,
	fetchEmailSignatures,
	createEmailSignature,
	updateEmailSignature,
	deleteEmailSignature,
	setDefaultSignature,
} from '../../../api/communications/emailSignatureApi';
import { User } from '../../../types/admin/UserTypes';
import { fetchClinicCompany } from '../../../api/admin/companyApi';

const { Text } = Typography;

const EmailSignaturesTab: React.FC = () => {
	const [signatures, setSignatures] = useState<EmailSignature[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingSignature, setEditingSignature] = useState<EmailSignature | null>(null);
	const [signatureName, setSignatureName] = useState('');
	const [signatureContent, setSignatureContent] = useState('');
	const [companyId, setCompanyId] = useState<number>(0);
	const [editorKey, setEditorKey] = useState<number>(0);
	const [isEditorReady, setIsEditorReady] = useState<boolean>(false);

	useEffect(() => {
		const load = async () => {
			let company = await fetchClinicCompany();
			setCompanyId(company.id);
			loadSignatures(company.id);
		};

		load();
	}, []);

	const loadSignatures = async (compId: number) => {
		setIsLoading(true);
		try {
			const data = await fetchEmailSignatures(compId);
			setSignatures(data);
		} catch (error) {
			message.error('Failed to load email signatures');
		} finally {
			setIsLoading(false);
		}
	};

	const handleCreate = () => {
		setEditingSignature(null);
		setSignatureName('');
		setIsEditorReady(false);
		setEditorKey((prev) => prev + 1);
		setIsModalOpen(true);
		setTimeout(() => {
			setSignatureContent('');
			setIsEditorReady(true);
		}, 50);
	};

	const handleEdit = (signature: EmailSignature) => {
		setEditingSignature(signature);
		setSignatureName(signature.name);
		setIsEditorReady(false);
		setEditorKey((prev) => prev + 1);
		setIsModalOpen(true);
		setTimeout(() => {
			setSignatureContent(signature.content);
			setIsEditorReady(true);
		}, 50);
	};

	const handleSave = async () => {
		if (!signatureName.trim()) {
			message.error('Please enter a signature name');
			return;
		}

		if (!signatureContent.trim()) {
			message.error('Please enter signature content');
			return;
		}

		setIsLoading(true);
		try {
			if (editingSignature) {
				await updateEmailSignature(editingSignature.id, {
					name: signatureName,
					content: signatureContent,
				});
				message.success('Signature updated successfully');
			} else {
				const user = getSessionUser() as User;
				await createEmailSignature({
					company_id: companyId,
					name: signatureName,
					content: signatureContent,
					created_by_user_id: user.id,
				});
				message.success('Signature created successfully');
			}
			setIsModalOpen(false);
			loadSignatures(companyId);
		} catch (error) {
			message.error('Failed to save signature');
		} finally {
			setIsLoading(false);
		}
	};

	const handleDelete = async (id: number) => {
		setIsLoading(true);
		try {
			await deleteEmailSignature(id);
			message.success('Signature deleted successfully');
			loadSignatures(companyId);
		} catch (error) {
			message.error('Failed to delete signature');
		} finally {
			setIsLoading(false);
		}
	};

	const handleSetDefault = async (id: number) => {
		setIsLoading(true);
		try {
			await setDefaultSignature(id, companyId);
			message.success('Default signature updated');
			loadSignatures(companyId);
		} catch (error) {
			message.error('Failed to set default signature');
		} finally {
			setIsLoading(false);
		}
	};

	const quillModules = useMemo(
		() => ({
			toolbar: [
				[{ header: [1, 2, 3, false] }],
				['bold', 'italic', 'underline', 'strike'],
				[{ list: 'ordered' }, { list: 'bullet' }],
				[{ color: [] }, { background: [] }],
				['link', 'image'],
				['clean'],
			],
		}),
		[]
	);

	const columns: ColumnsType<EmailSignature> = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			sorter: (a, b) => a.name.localeCompare(b.name),
			render: (text, record) => (
				<Space>
					{text}
					{record.is_default && <Tag color="blue">Default</Tag>}
				</Space>
			),
		},
		{
			title: 'Preview',
			dataIndex: 'content',
			key: 'content',
			ellipsis: true,
			render: (content: string) => {
				const stripped = content.replace(/<[^>]*>/g, '');
				return <Text type="secondary">{stripped.substring(0, 100)}...</Text>;
			},
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 200,
			render: (_, record) => (
				<Space>
					<Button
						type="text"
						icon={record.is_default ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
						onClick={() => handleSetDefault(record.id)}
						disabled={record.is_default}
						title={record.is_default ? 'Default signature' : 'Set as default'}
					/>
					<Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
					<Popconfirm
						title="Delete signature"
						description="Are you sure you want to delete this signature?"
						onConfirm={() => handleDelete(record.id)}
						okText="Yes"
						cancelText="No"
					>
						<Button type="text" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<>
			<Card
				size="small"
				title="Email Signatures"
				extra={
					<Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} size="small">
						Create Signature
					</Button>
				}
			>
				<Table
					columns={columns}
					dataSource={signatures}
					rowKey="id"
					loading={isLoading}
					size="small"
					pagination={{
						pageSize: 20,
						showSizeChanger: false,
						showTotal: (total) => `Total ${total} signatures`,
					}}
				/>
			</Card>

			<Modal
				title={editingSignature ? 'Edit Email Signature' : 'Create Email Signature'}
				open={isModalOpen}
				onCancel={() => setIsModalOpen(false)}
				onOk={handleSave}
				width={800}
				confirmLoading={isLoading}
			>
				<Space direction="vertical" style={{ width: '100%' }} size="middle">
					<div>
						<Text strong style={{ display: 'block', marginBottom: 8 }}>
							Signature Name <span style={{ color: '#ff4d4f' }}>*</span>
						</Text>
						<Input
							placeholder="e.g., Company Standard Signature"
							value={signatureName}
							onChange={(e) => setSignatureName(e.target.value)}
							maxLength={255}
						/>
					</div>

					<div>
						<Text strong style={{ display: 'block', marginBottom: 8 }}>
							Signature Content <span style={{ color: '#ff4d4f' }}>*</span>
						</Text>
						<div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
						{isEditorReady && (
							<ReactQuill
								key={editorKey}
								theme="snow"
								value={signatureContent}
								onChange={setSignatureContent}
								modules={quillModules}
								placeholder="Enter your email signature..."
								style={{ minHeight: 250, background: 'white' }}
							/>
						)}
						</div>
					</div>

					<div
						style={{
							padding: 12,
							background: 'var(--ant-color-primary-bg)',
							borderRadius: 6,
							border: '1px solid var(--ant-color-primary-border)',
						}}
					>
						<Text type="secondary" style={{ fontSize: 12 }}>
							💡 This signature will be automatically attached to the bottom of emails. You can include images, links, and formatting.
						</Text>
					</div>
				</Space>
			</Modal>
		</>
	);
};

export default EmailSignaturesTab;